import {useCallback, useEffect, useRef, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";

import {API} from "api";
import {openErrorDialog} from "layout/ErrorDialog";
import {
  initBgListEditor,
  reduceBgListEditor,
  toBgListWriteEntries,
  type BgListEditorAction,
  type BgListEditorState,
  type BgListEntryLike,
  type BgListSnapshot,
} from "libs/bgListEditor";
import {getBgListQueryKey, useBgList} from "hooks/useBgList";

/**
 * The stateful half of the background-list editor: the history lives here, the
 * rules live in `libs/bgListEditor`.
 *
 * Every action is optimistic. It rewrites the React Query entry the whole page
 * reads (App sources the display list, the tile count and the crop layer from
 * it), then PUTs the arrangement through a per-scope WRITE QUEUE — one PUT in
 * flight, the newest snapshot next, intermediate ones coalesced away. Racing
 * PUTs are how the server used to end up storing an OLDER arrangement than the
 * one on screen. A failed PUT resyncs the editor from the server instead of
 * rolling back: a rollback to that action's snapshot silently discarded every
 * later edit.
 *
 * The editor is scoped to (channel, type, product) — the same identity every
 * stored arrangement has. Whenever the scope changes the whole state resets
 * and waits for that scope's server list; keeping the old state alive is how
 * one product's tombstones (and, mid-fetch, its whole arrangement) used to be
 * written into another product's draft.
 */

export interface BgListEditor {
  /** The list as currently displayed. */
  list: BgListEntryLike[];
  canUndo: boolean;
  canRedo: boolean;
  /** True while a write is in flight or queued. */
  isSaving: boolean;
  reorder: (fromKey: string, toKey: string) => BgListEntryLike[];
  remove: (entryKey: string) => BgListEntryLike[];
  addUpload: (entry: BgListEntryLike) => BgListEntryLike[];
  undo: () => BgListEntryLike[];
  redo: () => BgListEntryLike[];
  /** Discard the draft arrangement — back to the published order. */
  reset: () => Promise<BgListEntryLike[]>;
  /** Publish the current arrangement for every user (admin only). */
  publish: () => Promise<void>;
}

/**
 * Write an optimistic list into the React Query cache and record the array the
 * cache ACTUALLY stored. `setQueryData` applies structural sharing, so the
 * stored `entries` can be a different array instance than the one passed in —
 * tracking only the input reference lets the editor's own write come back
 * through `useBgList` looking like a fresh server list, which resyncs the
 * editor and wipes the undo history on every edit.
 */
export function writeBgListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: readonly unknown[],
  list: BgListEntryLike[],
  removed: string[],
  ownWrites: WeakSet<object>
): void {
  ownWrites.add(list);
  queryClient.setQueryData(queryKey, {entries: list, removed});
  const stored = queryClient.getQueryData<{entries?: BgListEntryLike[]}>(queryKey)?.entries;
  if (stored) ownWrites.add(stored);
}

/** One queued write: the snapshot to persist plus the scope it belongs to.
 * The scope is captured at enqueue time so a write drains into ITS list even
 * when the user has already switched products. */
interface QueuedBgListWrite {
  snapshot: BgListSnapshot;
  channel: string;
  type: string;
  product: string;
}

export function useBgListEditor({
  channel,
  type,
  product,
  enabled,
}: {
  channel: string | undefined;
  type: string | undefined;
  product: string | undefined;
  enabled: boolean;
}): BgListEditor {
  const queryClient = useQueryClient();
  const {data} = useBgList({channel, type, product, enabled});
  const serverList: BgListEntryLike[] | undefined = data?.entries;
  const serverRemoved: string[] | undefined = data?.removed;

  const [state, setState] = useState<BgListEditorState>(() => initBgListEditor([]));
  const [isSaving, setIsSaving] = useState(false);
  // The list arrays this hook itself pushed into the cache. Without them, our
  // own optimistic write would come back as "a new server list" and reset the
  // editor — wiping the undo history on every edit.
  const ownWritesRef = useRef(new WeakSet<object>());
  const syncedRef = useRef<BgListEntryLike[] | null>(null);
  // The write queue: at most one PUT in flight, at most one pending snapshot
  // (always the newest — an intermediate arrangement nobody needs is dropped).
  const writeRef = useRef<{inFlight: boolean; pending: QueuedBgListWrite | null}>({
    inFlight: false,
    pending: null,
  });

  // The identity of the arrangement being edited. Everything scope-local —
  // state, history, the synced-list marker — dies with the old scope.
  const scopeKey = `${channel ?? ""} ${type ?? ""} ${product ?? ""}`;
  const scopeRef = useRef(scopeKey);
  if (scopeRef.current !== scopeKey) {
    scopeRef.current = scopeKey;
    syncedRef.current = null;
    setState(initBgListEditor([]));
  }

  useEffect(() => {
    if (!serverList || serverList === syncedRef.current) return;
    if (ownWritesRef.current.has(serverList)) return;
    syncedRef.current = serverList;
    setState((current) =>
      reduceBgListEditor(current, {type: "sync", list: serverList, removed: serverRemoved ?? []})
    );
  }, [serverList, serverRemoved]);

  const queryKey = getBgListQueryKey(channel, type, product);

  const writeCache = useCallback(
    (list: BgListEntryLike[], removed: string[]) => {
      writeBgListCache(queryClient, queryKey, list, removed, ownWritesRef.current);
    },
    // queryKey is a fresh array each render; its members are the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, channel, type, product]
  );

  /** Send queued writes one at a time; always the newest snapshot. */
  const drainWrites = useCallback(() => {
    const queue = writeRef.current;
    if (queue.inFlight || !queue.pending) return;
    const write = queue.pending;
    queue.pending = null;
    queue.inFlight = true;
    setIsSaving(true);
    API.putBgList({
      channel: write.channel,
      type: write.type,
      product: write.product,
      entries: toBgListWriteEntries(write.snapshot),
      removed: write.snapshot.removed,
    })
      .catch((err: any) => {
        // Do NOT roll back to this write's pre-image: later edits are already
        // on screen and a rollback would silently discard them. Drop what is
        // queued for this scope and resync the editor from the server truth.
        if (queue.pending && queue.pending.channel === write.channel && queue.pending.type === write.type && queue.pending.product === write.product) {
          queue.pending = null;
        }
        syncedRef.current = null;
        queryClient.invalidateQueries({
          queryKey: getBgListQueryKey(write.channel, write.type, write.product),
        });
        openErrorDialog({
          title: "Couldn't save the background list",
          summary:
            "The server didn't accept the change. The list was reloaded to match what is actually saved.",
          diagnostics: err?.envelope,
        });
      })
      .finally(() => {
        queue.inFlight = false;
        if (queue.pending) {
          drainWrites();
        } else {
          setIsSaving(false);
        }
      });
  }, [queryClient]);

  /** Apply one action: optimistic cache write, then queue the persist. */
  const apply = useCallback(
    (action: BgListEditorAction): BgListEntryLike[] => {
      const previous = state;
      const next = reduceBgListEditor(previous, action);
      if (next === previous) return previous.present.list;

      setState(next);
      writeCache(next.present.list, next.present.removed);

      // No full scope ⇒ nothing to persist against; the gate keeps editing
      // controls hidden in that state, this is the last line of defence.
      if (!channel || !type || !product) return next.present.list;
      writeRef.current.pending = {snapshot: next.present, channel, type, product};
      drainWrites();
      return next.present.list;
    },
    [state, writeCache, channel, type, product, drainWrites]
  );

  const reset = useCallback(async (): Promise<BgListEntryLike[]> => {
    if (!channel || !type || !product) return state.present.list;
    setIsSaving(true);
    try {
      const response = await API.resetBgList(channel, type, product);
      const list: BgListEntryLike[] = response.entries;
      const removed: string[] = response.removed ?? [];
      syncedRef.current = list;
      setState(initBgListEditor(list, removed));
      queryClient.setQueryData(queryKey, {entries: list, removed});
      return list;
    } catch (err: any) {
      openErrorDialog({
        title: "Couldn't reset the background list",
        summary: "The published order couldn't be restored. Try again in a moment.",
        diagnostics: err?.envelope,
      });
      return state.present.list;
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, type, product, queryClient, state]);

  const publish = useCallback(async (): Promise<void> => {
    if (!channel || !type || !product) return;
    setIsSaving(true);
    try {
      await API.publishBgListGlobal({
        channel,
        type,
        product,
        entries: toBgListWriteEntries(state.present),
        removed: state.present.removed,
      });
      // The publish deletes the admin's own draft server-side and rewrites the
      // published overlay, so every background query is now stale.
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ["bg-list"]}),
        queryClient.invalidateQueries({queryKey: ["v3-backgrounds"]}),
        queryClient.invalidateQueries({queryKey: ["get-backgrounds"]}),
      ]);
    } catch (err: any) {
      openErrorDialog({
        title: "Couldn't publish the background list",
        summary:
          err?.status === 403
            ? "Publishing the background list requires the admin role."
            : "The list wasn't published. Nothing changed for other users.",
        diagnostics: err?.envelope,
      });
    } finally {
      setIsSaving(false);
    }
  }, [channel, type, product, queryClient, state]);

  return {
    list: state.present.list,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    isSaving,
    reorder: (fromKey, toKey) => apply({type: "reorder", fromKey, toKey}),
    remove: (entryKey) => apply({type: "remove", entryKey}),
    addUpload: (entry) => apply({type: "add", entry}),
    undo: () => apply({type: "undo"}),
    redo: () => apply({type: "redo"}),
    reset,
    publish,
  };
}
